import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Search, Loader2, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductSuggestButton = ({ productName, errorText, token, onFixed }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [addAlias, setAddAlias] = useState(true);
  const [searchTimer, setSearchTimer] = useState(null);
  const [showAddNew, setShowAddNew] = useState(false);
  // Inline "Add New Product" form — used when no matching suggestion exists.
  // Historically this only had name + price, but finance also needs to fill
  // packing / unit / category etc. so the new product downstream costs and
  // reports correctly.
  const [newProduct, setNewProduct] = useState({
    product_name: "",
    price: "",
    box_packaging: "",
    weight_per_strip: "",
    unit: "",
    company: "",
    generic_name: "",
    category: "",
    branded_mrp: "",
  });
  const [addingProduct, setAddingProduct] = useState(false);

  const wrongName = productName || (errorText?.includes(":") ? errorText.split(":").slice(1).join(":").trim() : "");

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/suggest-product?name=${encodeURIComponent(wrongName)}&search=${encodeURIComponent(q)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setResults((await res.json()).suggestions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wrongName, token]);

  if (!wrongName) return null;

  const handleSearchChange = (val) => {
    setQuery(val);
    setShowAddNew(false);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(() => doSearch(val), 300));
  };

  const handleFix = async (correctName) => {
    setFixing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/bulk-fix-product`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wrong_name: wrongName, correct_name: correctName, add_alias: addAlias })
      });
      const data = await res.json();
      if (res.ok) {
        let msg = `Fixed ${data.updated} orders: "${wrongName}" → "${correctName}"`;
        if (data.alias_added) msg += " (alias saved for future)";
        toast.success(msg);
        setOpen(false);
        onFixed?.();
      } else { toast.error(data.detail || "Fix failed"); }
    } catch (e) { toast.error(e.message); }
    finally { setFixing(false); }
  };

  // Pydantic 422 errors return `detail` as a list of {loc,msg,type}. Rendering
  // that raw would crash React ("blank screen"). Always coerce to a string
  // before passing to toast/UI.
  const toErrorText = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
    }
    if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
    return String(detail);
  };

  const handleAddNewProduct = async () => {
    const name = (newProduct.product_name || "").trim();
    const price = String(newProduct.price || "").trim();
    if (!name || !price) {
      toast.error("Product name and price are required");
      return;
    }
    setAddingProduct(true);
    try {
      // Step 1: Add the new product to the global product list. Backend
      // `ProductCreate` schema wants strings for every field, including
      // `price` — sending a number here previously threw a Pydantic 422
      // whose {loc,msg,type} array got rendered into React and blanked the
      // screen.
      const body = {
        product_name: name.toUpperCase(),
        price,                              // <-- STRING now, not parseFloat
        second_name: wrongName.toUpperCase(),
        box_packaging: (newProduct.box_packaging || "").trim(),
        weight_per_strip: (newProduct.weight_per_strip || "").trim(),
        unit: (newProduct.unit || "").trim(),
        company: (newProduct.company || "").trim(),
        generic_name: (newProduct.generic_name || "").trim(),
        category: (newProduct.category || "").trim(),
        branded_mrp: String(newProduct.branded_mrp || "").trim(),
      };
      const addRes = await fetch(`${BACKEND_URL}/api/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        toast.error(toErrorText(err.detail, "Failed to add product"));
        return;
      }

      toast.success(`Product "${name.toUpperCase()}" added (₹${price})`);

      // Step 2: Auto-fix the error by mapping wrong name to new product
      await handleFix(name.toUpperCase());

      setShowAddNew(false);
      setNewProduct({
        product_name: "", price: "", box_packaging: "", weight_per_strip: "",
        unit: "", company: "", generic_name: "", category: "", branded_mrp: "",
      });
    } catch (e) {
      toast.error("Failed to add product: " + (e?.message || String(e)));
    } finally {
      setAddingProduct(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && !query) { setQuery(wrongName.split(" ")[0]); doSearch(wrongName.split(" ")[0]); } if (!o) { setShowAddNew(false); } }}>
      <PopoverTrigger asChild>
        <button className="ml-1 text-[10px] text-blue-600 hover:text-blue-800 underline shrink-0" data-testid="suggest-btn">Fix</button>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] p-3 max-h-[80vh] overflow-y-auto" align="end">
        <p className="text-xs text-red-600 mb-2 font-medium" title={wrongName}>
          Not found: <span className="break-words">{wrongName.length > 50 ? wrongName.substring(0, 50) + "..." : wrongName}</span>
        </p>
        <div className="relative mb-2">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
          <Input className="pl-7 h-8 text-xs" placeholder="Search correct product..." value={query} onChange={(e) => handleSearchChange(e.target.value)} autoFocus data-testid="product-search-input" />
        </div>
        {loading && <div className="flex items-center gap-2 py-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" />Searching...</div>}
        
        {/* No results found — show Add New Product option */}
        {!loading && results.length === 0 && query.length >= 2 && !showAddNew && (
          <div className="py-2 space-y-2">
            <p className="text-xs text-slate-400">No products found for "{query}"</p>
            <button
              onClick={() => {
                setShowAddNew(true);
                setNewProduct((p) => ({ ...p, product_name: wrongName.toUpperCase(), price: "" }));
              }}
              className="w-full flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border border-dashed border-emerald-300 text-emerald-600 hover:bg-emerald-50 text-xs font-medium transition-colors"
              data-testid="add-new-product-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add New Product
            </button>
          </div>
        )}
        
        {/* Add New Product Form — full field set so the newly-created product
            downstream costs / weighs correctly. Two-column grid keeps the
            popover from getting too tall. */}
        {showAddNew && (
          <div className="mb-2 p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 space-y-2">
            <p className="text-xs font-medium text-emerald-800">Add New Product</p>

            {/* Required row: Product Name (full width) */}
            <div>
              <label className="text-[10px] text-slate-500">Product Name <span className="text-red-500">*</span></label>
              <Input
                className="h-7 text-xs mt-0.5"
                value={newProduct.product_name}
                onChange={(e) => setNewProduct((p) => ({ ...p, product_name: e.target.value }))}
                placeholder="e.g. ELIMENTA 10 MG"
                data-testid="new-product-name-input"
              />
            </div>

            {/* Required row: Price + Branded MRP */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500">Price (₹) <span className="text-red-500">*</span></label>
                <Input
                  type="number"
                  step="0.01"
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct((p) => ({ ...p, price: e.target.value }))}
                  placeholder="Price"
                  data-testid="new-product-price-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Branded MRP</label>
                <Input
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.branded_mrp}
                  onChange={(e) => setNewProduct((p) => ({ ...p, branded_mrp: e.target.value }))}
                  placeholder="Optional"
                  data-testid="new-product-branded-mrp-input"
                />
              </div>
            </div>

            {/* Packing details */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-slate-500">Box Packaging</label>
                <Input
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.box_packaging}
                  onChange={(e) => setNewProduct((p) => ({ ...p, box_packaging: e.target.value }))}
                  placeholder="10x10"
                  data-testid="new-product-box-packaging-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Weight/Strip</label>
                <Input
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.weight_per_strip}
                  onChange={(e) => setNewProduct((p) => ({ ...p, weight_per_strip: e.target.value }))}
                  placeholder="60g"
                  data-testid="new-product-weight-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Unit</label>
                <Input
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.unit}
                  onChange={(e) => setNewProduct((p) => ({ ...p, unit: e.target.value }))}
                  placeholder="10"
                  data-testid="new-product-unit-input"
                />
              </div>
            </div>

            {/* Classification */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500">Company</label>
                <Input
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.company}
                  onChange={(e) => setNewProduct((p) => ({ ...p, company: e.target.value }))}
                  placeholder="Optional"
                  data-testid="new-product-company-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500">Category</label>
                <Input
                  className="h-7 text-xs mt-0.5"
                  value={newProduct.category}
                  onChange={(e) => setNewProduct((p) => ({ ...p, category: e.target.value }))}
                  placeholder="e.g. ANTIBIOTICS"
                  data-testid="new-product-category-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-slate-500">Generic Name</label>
              <Input
                className="h-7 text-xs mt-0.5"
                value={newProduct.generic_name}
                onChange={(e) => setNewProduct((p) => ({ ...p, generic_name: e.target.value }))}
                placeholder="Optional"
                data-testid="new-product-generic-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs flex-1"
                onClick={() => setShowAddNew(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={addingProduct || !newProduct.product_name.trim() || !String(newProduct.price).trim()}
                onClick={handleAddNewProduct}
                data-testid="confirm-add-product-btn"
              >
                {addingProduct ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add & Fix"}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400">This adds the product globally and fixes all matching orders.</p>
          </div>
        )}
        
        {results.length > 0 && (
          <>
            <div className="space-y-1.5 max-h-64 overflow-y-auto mb-2">
              {results.map((s, i) => (
                <div key={i} className={`flex items-center justify-between rounded px-2 py-2 border gap-3 ${s.is_hidden ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100 hover:bg-blue-50'}`}>
                  <div className="text-xs min-w-0 flex-1">
                    <span className="font-medium block leading-snug" style={{ wordBreak: 'break-word' }}>
                      {s.product_name?.length > 50 ? s.product_name.substring(0, 50) : s.product_name}
                      {s.is_hidden && <span className="ml-1 text-[8px] bg-amber-200 text-amber-700 px-1 rounded">HIDDEN</span>}
                    </span>
                    <span className="text-slate-500 block mt-0.5" style={{ wordBreak: 'break-word' }}>
                      {s.second_name && s.second_name !== s.product_name ? (s.second_name.length > 40 ? s.second_name.substring(0, 40) + "..." : s.second_name) + " | " : ""}
                      ₹{s.price || 0}
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-3 shrink-0 font-medium" disabled={fixing} onClick={() => handleFix(s.product_name)}>
                    {fixing ? "..." : "Apply"}
                  </Button>
                </div>
              ))}
            </div>
            {/* Add New button even when results exist but none match */}
            {!showAddNew && (
              <button
                onClick={() => {
                  setShowAddNew(true);
                  setNewProduct((p) => ({ ...p, product_name: wrongName.toUpperCase(), price: "" }));
                }}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 mb-2 rounded border border-dashed border-slate-300 text-slate-500 hover:text-emerald-600 hover:border-emerald-300 text-[10px] font-medium transition-colors"
                data-testid="add-new-product-alt-btn"
              >
                <Plus className="w-3 h-3" /> Not found? Add New Product
              </button>
            )}
          </>
        )}
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pt-2 border-t border-slate-100">
          <input type="checkbox" checked={addAlias} onChange={() => setAddAlias(!addAlias)} className="w-3.5 h-3.5 rounded" />
          Save as alias (auto-match next upload)
        </label>
      </PopoverContent>
    </Popover>
  );
};

export default ProductSuggestButton;
